"use client";

import { useEffect, useRef, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useMessages } from "@/lib/i18n/client";
import messages from "./ThemeToggle.i18n";

type Theme = "light" | "dark";

function getInitial(): Theme {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem("ciudad-viva-theme") as Theme | null;
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function apply(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}

export function ThemeToggle({ className = "" }: { className?: string }) {
  const [theme, setTheme] = useState<Theme>("light");
  const [animate, setAnimate] = useState(false);
  const rafRef = useRef<number>(0);
  const t = useMessages(messages);

  useEffect(() => {
    const initial = getInitial();
    // El tema guardado (localStorage) solo se conoce en el navegador; leerlo
    // en el render rompería la hidratación.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTheme(initial);
    apply(initial);
    // Enable animations only after the initial state settles to avoid
    // entrance animation on every page navigation
    rafRef.current = requestAnimationFrame(() => setAnimate(true));

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem("ciudad-viva-theme")) {
        const next: Theme = e.matches ? "dark" : "light";
        setTheme(next);
        apply(next);
      }
    };
    mq.addEventListener("change", onChange);
    return () => {
      cancelAnimationFrame(rafRef.current);
      mq.removeEventListener("change", onChange);
    };
  }, []);

  const toggle = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setTheme(next);
    apply(next);
    localStorage.setItem("ciudad-viva-theme", next);
  };

  const isDark = theme === "dark";

  return (
    <button
      onClick={toggle}
      aria-label={isDark ? t.toLight : t.toDark}
      title={isDark ? t.light : t.dark}
      suppressHydrationWarning
      className={`relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-border bg-surface text-foreground transition-colors hover:border-foreground/30 ${className}`}
    >
      <Sun
        className={`absolute h-4 w-4 ${animate ? "transition-all duration-500" : ""} ${
          isDark ? "-translate-y-6 rotate-90 opacity-0" : "translate-y-0 rotate-0 opacity-100"
        }`}
      />
      <Moon
        className={`absolute h-4 w-4 ${animate ? "transition-all duration-500" : ""} ${
          isDark ? "translate-y-0 rotate-0 opacity-100" : "translate-y-6 -rotate-90 opacity-0"
        }`}
      />
    </button>
  );
}
